import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import useSkinnyBanners from 'components/skinny-banner/useSkinnyBanner'
import compose from 'recompose/compose'
import { withI18next } from 'lib/i18n/withI18next'
import SlideContent from './slide-content'
import HeroPosterPreload from './HeroPosterPreload'
import useDesktopHeroOffset from './useDesktopHeroOffset'
import RetryModal from './retry-modal'
import styles from './style.scss'
import BottomBubbleNavigations from './bubble-navigations/bottom-bubble-navigations/BottomBubbleNavigations'

const EditorialSlides = ({
  t,
  user,
  isPhone,
  homefeed,
  isGuestMode,
  homePageBanners,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [apiResError, setApiResError] = useState(false)
  const heroRef = useDesktopHeroOffset(isPhone)
  const [banners, isFetched] = useSkinnyBanners(
    isGuestMode,
    t,
    user?.customer_groups,
  )
  const isHasSkinnyBanner = Boolean(
    isFetched && banners.length > 0 && banners?.[0]?.active_from,
  )
  const shouldShowContent =
    !apiResError && !isLoading && homePageBanners?.length && isFetched
  const shouldShowPlaceholder =
    !apiResError && (isLoading || !homePageBanners || !isFetched)
  const shouldShowGradient = homePageBanners?.[0]?.show_gradient

  useEffect(() => {
    setIsLoading(homefeed?.isFetching)
    setApiResError(Boolean(homefeed?.error))
  }, [homefeed])

  // finish loading and has no slide available
  if (!isLoading && !homePageBanners?.length) {
    return null
  }

  return (
    <div
      ref={heroRef}
      className={clsx({
        'slider-media-container-mobile': isPhone,
        'slider-media-container-desktop': !isPhone,
        'slider-media-container-desktop__with-skinny-banner':
          !isPhone && (!isFetched || isHasSkinnyBanner),
        'slider-media-container-mobile__with-skinny-banner':
          isPhone && isHasSkinnyBanner,
        'slider-media-container-desktop__with-gradient':
          !isPhone && shouldShowGradient,
        'slider-media-container-mobile__with-gradient':
          isPhone && shouldShowGradient,
      })}
    >
      <style jsx>{styles}</style>
      {!apiResError && (
        <HeroPosterPreload banner={homePageBanners?.[0]} isPhone={isPhone} />
      )}
      {apiResError && (
        <RetryModal
          setApiResError={setApiResError}
          setIsLoading={setIsLoading}
        />
      )}
      {shouldShowPlaceholder && <div className="hero-banner__skeleton" />}
      {shouldShowContent && (
        <React.Fragment>
          <SlideContent />
          <BottomBubbleNavigations />
        </React.Fragment>
      )}
    </div>
  )
}

EditorialSlides.propTypes = {
  t: PropTypes.func.isRequired,
  isPhone: PropTypes.bool.isRequired,
  slides: PropTypes.shape({ sliders: PropTypes.arrayOf(PropTypes.shape({})) }),
  isGuestMode: PropTypes.bool,
  user: PropTypes.shape({
    id_customer: PropTypes.number,
    customer_groups: PropTypes.arrayOf(PropTypes.number),
  }),
  homefeed: PropTypes.shape({
    isFetching: PropTypes.bool,
    error: PropTypes.string,
  }).isRequired,
  homePageBanners: PropTypes.arrayOf(
    PropTypes.shape({
      attributes: PropTypes.shape({
        media: PropTypes.shape({ kind: PropTypes.string }),
      }),
      show_gradient: PropTypes.bool,
    }),
  ),
}

EditorialSlides.defaultProps = {
  user: null,
  slides: null,
  isGuestMode: false,
  homePageBanners: [],
}

export default compose(
  connect((state) => ({
    user: state.auth.user,
    homefeed: state.homefeed,
    isPhone: state.device.isPhone,
    isGuestMode: state.auth.isGuestMode,
    slides: state.editorialSlides.payload,
    homePageBanners: state.homePage.banners,
  })),
  withI18next(),
)(EditorialSlides)
